import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../Components/EPrescriptionMedicamentsWrapper.module.scss';
import AddButton from '../../../Components/Button/AddButton';
import Button from '../../../Components/Button/Button';
import { Theme } from '../../../Enums/Theme.enum';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import EPrescriptionMedicamentsList from './EPrescriptionMedicamentsList';
import { EPrescriptionHVLPMedicament } from './EPrescriptionMedicamentsHVLPForm';
import { EPrescriptionIPLPMedicament } from './EPrescriptionMedicamentsIPLPForm';
import EPrescriptionMedicamentsModal from './EPrescriptionMedicamentsModal';
import { Size } from '../../../Enums/Size.enum';
import Text from '../../../Components/Base/Text/Text';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import { EPrescriptionWizardData } from './EPrescriptionWrapper';

interface EPrescriptionMedicamentsWrapperProps {
	patientId: string;
	onMedicamentsUpdate?: (values: EPrescriptionMedicament[]) => void;
	onSuccess?: (params?: any) => void;
	defaultValues?: {
		hvlp?: EPrescriptionHVLPMedicament[];
		iplp?: EPrescriptionIPLPMedicament[];
	};
	formValues?: EPrescriptionWizardData;
}

export type EPrescriptionMedicament =
	| EPrescriptionHVLPMedicament
	| EPrescriptionIPLPMedicament;

const getMergedMedicaments = (
	defaultValues?: EPrescriptionMedicamentsWrapperProps['defaultValues'],
): EPrescriptionMedicament[] => {
	return [...(defaultValues?.hvlp ?? []), ...(defaultValues?.iplp ?? [])];
};

const EPrescriptionMedicamentsWrapper: React.FC<
	EPrescriptionMedicamentsWrapperProps
> = ({
	patientId,
	defaultValues,
	onMedicamentsUpdate,
	onSuccess,
	formValues,
}) => {
	const [medicamentFormDefaults, setMedicamentFormDefaults] =
		useState<EPrescriptionMedicament>();
	const [isMedicamentDetailOpen, setIsMedicamentDetailOpen] = useState(false);
	const [selectedPrescriptionType, setSelectedPrescriptionType] = useState<
		PrescriptionType | undefined
	>();
	const initializedFromDefaultsRef = useRef(false);
	const isDirtyRef = useRef(false);
	const lastPropDefaultsRef = useRef<string>('');

	const [prescriptions, setPrescriptions] = useState<EPrescriptionMedicament[]>(
		() => getMergedMedicaments(defaultValues),
	);

	const mergedDefaultPrescriptions = useMemo(
		() => getMergedMedicaments(defaultValues),
		[defaultValues],
	);

	useEffect(() => {
		const nextSerialized = JSON.stringify(mergedDefaultPrescriptions);

		if (!initializedFromDefaultsRef.current) {
			initializedFromDefaultsRef.current = true;
			lastPropDefaultsRef.current = nextSerialized;
			setPrescriptions(mergedDefaultPrescriptions);
			return;
		}

		if (isDirtyRef.current) {
			return;
		}

		if (lastPropDefaultsRef.current !== nextSerialized) {
			lastPropDefaultsRef.current = nextSerialized;
			setPrescriptions(mergedDefaultPrescriptions);
		}
	}, [mergedDefaultPrescriptions]);

	const handleFormClick = (form: PrescriptionType) => {
		setMedicamentFormDefaults(undefined);
		setSelectedPrescriptionType(form);
		setIsMedicamentDetailOpen(true);
	};

	const handleClose = () => {
		setMedicamentFormDefaults(undefined);
		setSelectedPrescriptionType(undefined);
		setIsMedicamentDetailOpen(false);
	};

	const handleSubmit = (
		values: EPrescriptionHVLPMedicament | EPrescriptionIPLPMedicament,
	) => {
		isDirtyRef.current = true;

		setPrescriptions(prev => {
			const next = prev.filter(({ id }) => id !== values.id);
			return [...next, values];
		});
	};

	const handleRemoveMedicament = (item: EPrescriptionMedicament) => {
		isDirtyRef.current = true;

		setPrescriptions(prev =>
			prev.filter(medicament => medicament.id !== item.id),
		);
	};

	const handleEditMedicamentItem = (item: EPrescriptionMedicament) => {
		setMedicamentFormDefaults(item);
		setSelectedPrescriptionType(item.formType);
		setIsMedicamentDetailOpen(true);
	};

	return (
		<div className={styles.wrapper}>
			{isMedicamentDetailOpen && selectedPrescriptionType && (
				<EPrescriptionMedicamentsModal
					isOpen
					prescriptionType={selectedPrescriptionType}
					size={Size.SM}
					onClose={handleClose}
					onSubmit={handleSubmit}
					defaultValues={medicamentFormDefaults}
					patientId={patientId}
					isSelfPayer={
						formValues?.patientInformation?.insuranceCompany?.value === '999'
					}
				/>
			)}

			<Text tx={'base.meds'} className={styles.title} firstUpper />

			<div className={styles.options}>
				<AddButton
					title='component.ePrescription.addHVLP'
					onClick={() => handleFormClick(PrescriptionType.HVLP)}
				/>
				<AddButton
					title='component.ePrescription.addIPLP'
					onClick={() => handleFormClick(PrescriptionType.IPLP)}
				/>
			</div>

			<ScrollContainer>
				<EPrescriptionMedicamentsList
					medicaments={prescriptions}
					onRemove={handleRemoveMedicament}
					onEdit={handleEditMedicamentItem}
				/>

				<div className='d-flex mt-4 justify-content-between pr-3'>
					<Button
						theme={Theme.GREEN}
						title='base.shared.continue'
						className={styles.submitButton}
						onClick={() => {
							onMedicamentsUpdate?.(prescriptions);
							onSuccess?.();
						}}
					/>
				</div>
			</ScrollContainer>
		</div>
	);
};

export default EPrescriptionMedicamentsWrapper;
